// A solo race against the clock (DESIGN.md §5 v2): standing start, N laps,
// tyre compounds and wear, fuel burn, weather, damage, track-limit penalties
// and the 2026 race energy rules. Pit stops are driven by ./pit.ts.

import type { CarEnv, CarInput } from '../sim/car';
import { inZones, Session, type TrackLayout } from '../sim/session';
import { Damage } from './damage';
import { bestTyreFor, COMPOUNDS, DRY_COMPOUNDS, TyreSet, type Compound } from './tyres';
import { makePlan, Weather, type WeatherPlan, type WeatherPreset } from './weather';
import { applySetup, type CarSetup } from './setup';
import { PitStop } from './pit';
import type { PitLane } from '../track/pitLane';

/** Full race distance at Sepang: fewest laps over 305 km (B2.5.2). */
export const FULL_RACE_LAPS = 56;
/** Fuel already counted in carParams.mass (light qualifying load), kg. */
export const QUALI_FUEL_KG = 20;

export const raceModel = {
  /** Fuel for a full-distance race, kg, and burn per lap. */
  fullRaceFuelKg: 74,
  /** Throttle-seconds per lap at race pace (measured with scripts/race-sim.ts). */
  throttleSecondsPerLap: 81,
  /** Tyre work per lap at race pace (Σ grip use² · speed · dt), measured likewise. */
  tyreWorkPerLap: 2350,
  /** Pit lane time loss target, s (lane at 80 km/h vs racing past). */
  stationarySeconds: 2.4,
  noseChangeSeconds: 8,
  trackLimitWarnings: 3,
  trackLimitPenalty: 5,
  /** B6.3.6: DSQ in the rules; we apply a time penalty instead. */
  compoundRulePenalty: 30,
};

export interface RaceConfig {
  laps: number;
  weather: WeatherPreset;
  seed: number;
  startCompound: Compound;
  /** B6.3.6: use two different dry compounds unless inters or wets were used. */
  mandatoryCompounds: boolean;
  /** Optional explicit weather plan (challenges); overrides the preset. */
  plan?: WeatherPlan;
  damage?: boolean;
  setup?: CarSetup;
}

export type RacePhase = 'grid' | 'lights' | 'racing' | 'pit' | 'finished';

export interface Stint {
  compound: Compound;
  fromLap: number;
  laps: number;
}

export interface RadioMessage {
  text: string;
  kind: 'info' | 'warn' | 'box' | 'flag';
}

export interface RaceResult {
  laps: number;
  raceTime: number;
  penalties: number;
  total: number;
  bestLap: number | undefined;
  stints: Stint[];
  pitStops: number;
  compoundRuleBroken: boolean;
  lapTimes: number[];
}

/**
 * Wear, fuel and weather run faster in shorter races so strategy still matters
 * (the pit loss stays real): a 10- or 20-lap race is a natural one-stopper, a
 * full-distance race two stops (race-model.md §7).
 */
export function raceScale(laps: number): number {
  return Math.min(6, Math.max(1, (FULL_RACE_LAPS / Math.max(1, laps)) ** 0.9));
}

export class Race {
  phase: RacePhase = 'grid';
  readonly scale: number;
  readonly weather: Weather;
  readonly damage = new Damage();
  tyres: TyreSet;
  fuelKg: number;
  readonly fuelStartKg: number;
  raceTime = 0;
  penalties = 0;
  trackLimitStrikes = 0;
  stints: Stint[];
  pitStops = 0;
  lapTimes: number[] = [];
  /** Red lights lit (0–5) during the start sequence. */
  lightsLit = 0;
  readonly radio: RadioMessage[] = [];
  result: RaceResult | undefined;
  /** The pit lane, when the circuit has one (pit stops need it). */
  pitLane: PitLane | undefined;
  pitStop: PitStop | undefined;
  /** Tyres to fit at the next stop (chosen in the pit menu; preset to the engineer's call). */
  pitChoice: Compound = 'medium';
  /** Race time of the engineer's last "box" call, for the pit-entry prompt. */
  boxCallAt = -Infinity;
  /**
   * The player has asked to pit this lap. The entry at Sepang is on the outside
   * of T15, so without this, running wide there would take you into the pits.
   */
  boxArmed = false;

  private lightsTimer = 0;
  private lightsOutAt: number;
  private lastProgress = 0;
  private said = new Set<string>();
  private wasLowGrip: boolean;

  constructor(
    readonly session: Session,
    readonly config: RaceConfig,
  ) {
    this.scale = raceScale(config.laps);
    const plan = config.plan ?? makePlan(config.weather, config.laps, config.seed);
    // Weather runs only √scale faster: short races still get a drying phase to play.
    this.weather = new Weather(plan, Math.sqrt(this.scale));
    this.wasLowGrip = this.weather.lowGrip;
    this.tyres = new TyreSet(config.startCompound);
    this.stints = [{ compound: config.startCompound, fromLap: 1, laps: 0 }];
    const perLap = raceModel.fullRaceFuelKg / FULL_RACE_LAPS;
    // Fuel for the distance, with the effect scaled like tyre wear; small margin.
    this.fuelStartKg = Math.min(110, perLap * config.laps * this.scale * 1.08 + 1);
    this.fuelKg = this.fuelStartKg;
    // Lights: one per second, then a random 0.5–2.5 s hold (deterministic per seed).
    this.lightsOutAt = 5 + 0.5 + ((config.seed * 9301 + 49297) % 233280) / 233280 * 2;
    session.gridStart(8);
    session.car.pu.harvestLimitMJ = 8.5;
    session.envHook = (env) => this.adjustEnv(env);
    this.pitLane = session.layout.pitLane;
    this.applyCarMods();
  }

  get layout(): TrackLayout {
    return this.session.layout;
  }

  /** Current lap (1-based) while racing. */
  get lap(): number {
    return Math.max(1, Math.min(this.config.laps, this.session.lapNumber));
  }

  /** Race distance covered in laps (fractional). */
  get progress(): number {
    const L = this.layout.track.length;
    if (this.session.lapNumber < 1) return 0;
    return this.session.lapNumber - 1 + this.session.projection.s / L;
  }

  get total(): number {
    return this.raceTime + this.penalties;
  }

  get usedCompounds(): Set<Compound> {
    return new Set(this.stints.map((s) => s.compound));
  }

  /** True once the B6.3.6 tyre rule is satisfied (or doesn't apply). */
  get compoundRuleMet(): boolean {
    if (!this.config.mandatoryCompounds) return true;
    const used = this.usedCompounds;
    if (used.has('inter') || used.has('wet')) return true;
    return DRY_COMPOUNDS.filter((c) => used.has(c)).length >= 2;
  }

  /** Advance the race by one physics step. Returns false when the car is held (grid / finished). */
  step(input: CarInput, dt: number): boolean {
    if (this.phase === 'grid' || this.phase === 'lights') {
      this.lightsTimer += dt;
      this.phase = 'lights';
      this.lightsLit = Math.min(5, Math.floor(this.lightsTimer));
      if (this.lightsTimer >= this.lightsOutAt) {
        this.lightsLit = 0;
        this.phase = 'racing';
        this.say('go', 'Lights out, go go go!', 'flag');
      }
      // Engine noise only; the car stays on the grid.
      this.session.car.holdOnGrid(input, dt);
      return false;
    }
    if (this.phase === 'pit') {
      this.raceTime += dt;
      this.stepPit(dt);
      return true;
    }
    if (this.phase === 'finished') {
      // Cool-down: let the car roll on gently.
      this.session.step({ steer: input.steer, throttle: 0, brake: 0.15 }, dt);
      return true;
    }

    this.raceTime += dt;
    this.applyCarMods();
    this.session.step(input, dt);
    const car = this.session.car;

    // Tyre work: grip use squared, so sliding wears far more than smooth driving.
    const work = ((car.gripUseFront ** 2 + car.gripUseRear ** 2) / 2) * car.speed * dt / raceModel.tyreWorkPerLap;
    this.tyres.step(work, this.scale, this.weather.wetness, car.speed, dt);
    // Fuel: burn follows throttle.
    const burnPerThrottleSecond = raceModel.fullRaceFuelKg / FULL_RACE_LAPS / raceModel.throttleSecondsPerLap;
    this.fuelKg = Math.max(0, this.fuelKg - input.throttle * burnPerThrottleSecond * this.scale * dt);

    const progress = this.progress;
    const dLaps = Math.max(0, progress - this.lastProgress);
    this.lastProgress = progress;
    this.weather.step(progress, Math.min(dLaps, 0.01));

    if (this.config.damage !== false && this.session.impact > 0 && this.damage.impact(this.session.impact)) {
      this.say(`damage-${this.pitStops}`, this.damage.needsNose ? 'Front wing damage, we can change the nose at the stop.' : 'Some floor damage there, car will be a bit slower.', 'warn');
    }
    this.handleEvents();
    this.engineer();
    this.checkPitEntry();
    return true;
  }

  /** Simulations/tests: take the pit entry now, as if the player had steered onto it. */
  forcePit(): void {
    this.checkPitEntry(true);
  }

  private checkPitEntry(force = false): void {
    const lane = this.pitLane;
    if (!lane || this.phase !== 'racing') return;
    const p = this.session.projection;
    const car = this.session.car;
    const start = lane.project(car.x, car.z);
    // Armed: take the entry. Not armed but well down the entry road: clearly meant it.
    const deep = start.d > 120 && lane.isOnEntryRoad(car.x, car.z);
    if (!force && !deep && (!this.boxArmed || !lane.commitsToPit(p.s, p.offset))) return;
    this.boxArmed = false;
    this.pitChoice = this.suggestTyre();
    this.pitStop = new PitStop(lane, start.d, car.speed, () => this.serviceCar(this.pitChoice));
    this.phase = 'pit';
  }

  private stepPit(dt: number): void {
    const stop = this.pitStop!;
    const f = stop.step(dt);
    this.session.followScripted(f.x, f.z, f.heading, f.speed, dt);
    const progress = this.progress;
    const dLaps = Math.max(0, progress - this.lastProgress);
    this.lastProgress = progress;
    this.weather.step(progress, Math.min(dLaps, 0.01));
    this.handleEvents();
    if (stop.done && this.phase === 'pit') {
      this.phase = 'racing';
      this.pitStop = undefined;
    }
  }

  /** Engineer's tyre call for a stop now: the right tyre for the conditions, and the rules. */
  suggestTyre(): Compound {
    const lap = this.progress;
    const w = this.weather.wetness;
    // Look a lap ahead: rain arriving or stopping changes the call.
    const soon = this.weather.forecast(lap, 2, this.config.seed);
    const rainSoon = soon.some((f) => f.rain > 0.3);
    const wet = w > 0.55 || (this.weather.rain > 0.7 && w > 0.3);
    if (wet) return 'wet';
    if (w > 0.13 || rainSoon) return 'inter';
    const lapsLeft = this.config.laps - Math.floor(lap);
    const need = (c: Compound): boolean => (COMPOUNDS[c].cliffLaps / this.scale) * 0.95 >= lapsLeft;
    const used = this.usedCompounds;
    const options = DRY_COMPOUNDS.filter((c) => this.compoundRuleMet || !used.has(c));
    // Softest tyre that lasts to the flag; otherwise the hardest available.
    return options.find(need) ?? options[options.length - 1] ?? 'medium';
  }

  /** Toggle "box this lap" (the pit button). */
  toggleBox(): void {
    if (this.phase !== 'racing' || !this.pitLane) return;
    this.boxArmed = !this.boxArmed;
    this.radio.push({ text: this.boxArmed ? 'Copy, box this lap. Pit entry is on the right at the last corner.' : 'Okay, staying out.', kind: 'info' });
  }

  /**
   * Pit button state: offered from the back straight to the entry (and whenever
   * the engineer has called a stop), highlighted when called, lit when armed.
   */
  pitHint(): 'none' | 'approach' | 'box' | 'armed' {
    const lane = this.pitLane;
    if (!lane || this.phase !== 'racing' || this.lapTimes.length >= this.config.laps) return 'none';
    if (this.boxArmed) return 'armed';
    const L = this.layout.track.length;
    const s = this.session.projection.s;
    const before = (lane.entryS - s + L) % L;
    const called = this.raceTime - this.boxCallAt < 120;
    if (called) return 'box';
    return before < 1300 ? 'approach' : 'none';
  }

  /** Pit stop: new tyres (and nose if needed); returns the stationary time. */
  serviceCar(compound: Compound): number {
    let t = raceModel.stationarySeconds;
    if (this.damage.needsNose) t += raceModel.noseChangeSeconds;
    this.damage.repair();
    this.tyres = new TyreSet(compound, false);
    this.pitStops++;
    this.stints.push({ compound, fromLap: this.lap, laps: 0 });
    this.said.delete('tyres-old');
    this.said.delete('tyres-gone');
    return t;
  }

  private applyCarMods(): void {
    const car = this.session.car;
    const grip = this.tyres.grip(this.weather.wetness);
    car.mods.extraMass = this.fuelKg - QUALI_FUEL_KG;
    car.mods.gripFront = grip;
    car.mods.gripRear = grip;
    car.mods.downforce = this.damage.downforce;
    car.mods.drag = this.damage.drag;
    car.mods.balanceShift = this.damage.balanceShift;
    if (this.config.setup) applySetup(car.mods, this.config.setup);
  }

  private adjustEnv(env: CarEnv): void {
    const s = this.session.projection.s;
    if (this.weather.lowGrip) {
      // Wet-weather rules: front-wing-only Straight Mode, 250 kW deployment map.
      env.partialAero = true;
      env.deployCapKW = 250;
    } else if (this.layout.raceDeploySectors && inZones(s, this.layout.raceDeploySectors, this.layout.track.length)) {
      // Alt 1 curve: 250 kW below 310 km/h, where it meets the normal taper.
      env.deployCapKW = 250;
    }
  }

  private handleEvents(): void {
    for (const e of this.session.events) {
      if (e.kind === 'invalid') {
        // Heading down the pit entry road isn't a track-limits offence.
        const car = this.session.car;
        if (this.phase === 'pit' || this.pitLane?.isOnEntryRoad(car.x, car.z)) continue;
        this.trackLimitStrikes++;
        if (this.trackLimitStrikes === raceModel.trackLimitWarnings) {
          this.radio.push({ text: 'Black and white flag for track limits. Next one is a penalty.', kind: 'warn' });
        } else if (this.trackLimitStrikes > raceModel.trackLimitWarnings) {
          this.penalties += raceModel.trackLimitPenalty;
          this.radio.push({ text: `${raceModel.trackLimitPenalty}-second penalty for track limits.`, kind: 'warn' });
        }
      } else if (e.kind === 'lap' && e.lapTime !== undefined && this.session.lapNumber >= 1) {
        this.lapTimes.push(e.lapTime);
        this.tyres.laps++;
        this.stints[this.stints.length - 1]!.laps++;
        this.lapCompleted();
      }
    }
  }

  private lapCompleted(): void {
    const done = this.lapTimes.length;
    if (done >= this.config.laps) {
      this.finish();
      return;
    }
    if (done === this.config.laps - 1) this.radio.push({ text: 'Final lap. Bring it home.', kind: 'flag' });
    // Reminder about the tyre rule a few laps from the end.
    const lapsLeft = this.config.laps - done;
    if (!this.compoundRuleMet && lapsLeft <= Math.max(2, Math.round(this.config.laps * 0.25))) {
      this.say('compound-rule', 'Reminder: we still need to run a second dry compound. Box when you can.', 'box');
    }
  }

  /** Dev/testing: end the race now, padding unfinished laps with the current pace. */
  forceFinish(): void {
    if (this.phase === 'finished') return;
    const pace = this.lapTimes.length ? this.lapTimes[this.lapTimes.length - 1]! : 100;
    while (this.lapTimes.length < this.config.laps) {
      this.lapTimes.push(pace);
      this.raceTime += pace;
      this.stints[this.stints.length - 1]!.laps++;
    }
    this.finish();
  }

  private finish(): void {
    this.phase = 'finished';
    const broken = !this.compoundRuleMet;
    if (broken) this.penalties += raceModel.compoundRulePenalty;
    const valid = this.lapTimes.filter((t) => t > 0);
    this.result = {
      laps: this.config.laps,
      raceTime: this.raceTime,
      penalties: this.penalties,
      total: this.total,
      bestLap: valid.length ? Math.min(...valid) : undefined,
      stints: this.stints.map((s) => ({ ...s })),
      pitStops: this.pitStops,
      compoundRuleBroken: broken,
      lapTimes: [...this.lapTimes],
    };
    this.radio.push({ text: 'Chequered flag! Great job.', kind: 'flag' });
  }

  /** Race engineer: tyre, weather and rules calls, each said once per situation. */
  private engineer(): void {
    const t = this.tyres;
    const spec = COMPOUNDS[t.compound];
    if (t.wear > 0.8) this.say('tyres-old', `${spec.name}s are going off. Box this lap or next.`, 'box');
    if (t.wear > 1) this.say('tyres-gone', 'Tyres are past the cliff, box now!', 'box');

    const w = this.weather.wetness;
    const lap = this.progress;
    const change = this.weather.nextChange(lap, 2.5);
    if (change) {
      const inLaps = Math.max(1, Math.round(change.lap - lap));
      const key = `wx-${change.starts ? 'rain' : 'dry'}-${Math.round(change.lap)}`;
      this.say(key, change.starts ? `Rain expected in about ${inLaps} lap${inLaps > 1 ? 's' : ''}.` : `Rain should stop in about ${inLaps} lap${inLaps > 1 ? 's' : ''}.`, 'info');
    }
    const best = bestTyreFor(w);
    const mine = spec.slick ? 'slick' : t.compound;
    // Call it with a little margin past the crossover so it doesn't flip-flop.
    const every2 = Math.floor(lap / 2);
    if (mine === 'slick' && w > 0.16) this.say(`go-inters-${every2}`, 'Track is wet, inters are faster now. Box, box.', 'box');
    if (mine === 'inter' && w < 0.1 && this.weather.rain < 0.05) this.say(`go-slicks-${every2}`, 'Dry line is there. Slicks are the call, box.', 'box');
    if (mine === 'inter' && w > 0.6) this.say(`go-wets-${every2}`, 'Standing water. Full wets would be quicker.', 'box');
    if (mine === 'wet' && best !== 'wet' && w < 0.5) this.say(`wets-to-inters-${every2}`, 'Water is clearing, inters are the faster tyre now.', 'box');

    if (this.pitLane && this.pitHint() === 'approach') this.say('box-tip', 'To pit, tap BOX before the last corner, then take the pit entry on the right.', 'info');

    if (this.weather.lowGrip !== this.wasLowGrip) {
      this.wasLowGrip = this.weather.lowGrip;
      this.radio.push({
        text: this.weather.lowGrip ? 'Race control: Low Grip Conditions. Front wing only in Straight Mode.' : 'Race control: back to normal grip conditions.',
        kind: 'flag',
      });
    }
  }

  private say(key: string, text: string, kind: RadioMessage['kind']): void {
    if (this.said.has(key)) return;
    this.said.add(key);
    this.radio.push({ text, kind });
    if (kind === 'box') this.boxCallAt = this.raceTime;
  }
}
